import { useRef, useEffect, useMemo, useState } from 'react'
import { useMessagesStore } from '@/shared/stores/messages.store'
import { useTerminalStore } from '@/shared/stores/terminal.store'
import { trpc } from '@/shared/lib/trpc'
import { linkifyText } from '@/shared/lib/linkify'
import { useStickToBottom } from '@/shared/lib/useStickToBottom'
import type { TerminalEvent, TerminalEventWithSeq } from '../../../../shared/types/terminal-event.js'

const MAX_OUTPUT_LINES = 20

/**
 * Marqueur litteral du dump brut de metadata renvoye par le SDK au lancement d'un Agent
 * (Cowboy) en arriere-plan. Chaine verifiee 1re main dans le SDK installe :
 * /opt/motherboard/node_modules/@anthropic-ai/claude-agent-sdk/cli.js (`Async agent
 * launched successfully.\nagentId: ...`). Sert a distinguer ce tool_output specifique
 * (dump illisible : agentId interne, output_file, consignes "Do NOT Read") des autres
 * tool_output normaux, pour lui donner un rendu compact au lieu du dump brut (journal
 * #10687, task_start/task_notification + resume tool_output Agent).
 */
const AGENT_LAUNCH_MARKER = 'Async agent launched successfully'

/** Semantic color per tool category */
const TOOL_COLORS: Record<string, string> = {
  Bash: '#2ecc71',
  Read: '#3498db',
  Glob: '#3498db',
  Grep: '#3498db',
  Edit: '#e67e22',
  Write: '#e67e22',
  Agent: '#9b59b6',
  Task: '#9b59b6',
}

function getToolColor(toolName: string): string | undefined {
  return TOOL_COLORS[toolName]
}

/** Detect error patterns in tool output */
const ERROR_PATTERNS = /\b(Error|error|ENOENT|EACCES|EPERM|EISDIR|exit code [1-9]|ERR!|FATAL|fatal|panic|Traceback|SyntaxError|TypeError|ReferenceError|Cannot find|Module not found|Permission denied|command not found)\b/

function isErrorOutput(content: string): boolean {
  return ERROR_PATTERNS.test(content)
}

function truncateOutput(text: string): string {
  const lines = text.split('\n')
  if (lines.length <= MAX_OUTPUT_LINES) return text
  return lines.slice(0, MAX_OUTPUT_LINES).join('\n') + '\n...'
}

function TerminalTextBlock({ content }: { content: string }) {
  return <pre className="terminal-block terminal-text">{linkifyText(content)}</pre>
}

function TerminalToolStart({ toolName, input }: { toolName: string; input: string }) {
  const color = getToolColor(toolName)
  const nameStyle = color ? { color } : undefined
  const markerStyle = color ? { color } : undefined

  return (
    <div className="terminal-block terminal-tool-start">
      <span className="terminal-tool-marker" style={markerStyle}>{'▶'}</span>
      <span className="terminal-tool-name" style={nameStyle}>{toolName}</span>
      <pre className="terminal-tool-input">{colorizeFilePaths(input)}</pre>
    </div>
  )
}

/** Highlight file paths in tool input (absolute paths starting with /) */
const FILE_PATH_REGEX = /(\/[\w./-]+(?:\.\w+)?)/g

function colorizeFilePaths(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let key = 0

  FILE_PATH_REGEX.lastIndex = 0
  while ((match = FILE_PATH_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    parts.push(
      <span key={`fp-${key++}`} style={{ color: '#3498db' }}>{match[1]}</span>,
    )
    lastIndex = match.index + match[1].length
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }
  return parts.length > 0 ? parts : [text]
}

function TerminalToolOutput({ content }: { content: string }) {
  // Resume compact du dump brut de lancement d'Agent (cf. AGENT_LAUNCH_MARKER) : le
  // contenu normal (agentId interne, output_file, consignes "Do NOT Read") n'apporte
  // rien a l'affichage TERM et casse la lisibilite du flux — un bloc unique reutilisant
  // le style .terminal-tool-summary (deja existant, aucune classe CSS nouvelle) suffit.
  if (content.trim().startsWith(AGENT_LAUNCH_MARKER)) {
    return <div className="terminal-block terminal-tool-summary">{'Cowboy lancé en arrière-plan'}</div>
  }
  const truncated = truncateOutput(content)
  const hasError = isErrorOutput(truncated)
  const className = `terminal-block terminal-tool-output${hasError ? ' terminal-tool-output-error' : ''}`
  return <pre className={className}>{linkifyText(truncated)}</pre>
}

function TerminalToolProgress({ toolName, elapsedSeconds }: { toolName: string; elapsedSeconds: number }) {
  return (
    <div className="terminal-block terminal-tool-progress">
      <span className="terminal-progress-icon">{'⏳'}</span>
      <span>{toolName}</span>
      <span className="terminal-progress-time">({Math.round(elapsedSeconds)}s)</span>
    </div>
  )
}

function TerminalToolSummary({ summary }: { summary: string }) {
  return <div className="terminal-block terminal-tool-summary">{summary}</div>
}

/**
 * Suivi lancement/fin d'un cowboy (sous-agent) — journal #10687, diagnostic
 * debug-visibilite-cowboys-terminal-pulsar-2026-08-10 (#11507) : le daemon emet deja
 * task_start/task_notification, persistes et relayes jusqu'au store client sans filtre —
 * seul le rendu manquait (default: return null). Placement INLINE dans le flux terminal
 * (Ockham, cf. miniplan) : reutilise le layout existant .terminal-tool-progress (icone +
 * texte + temps) plutot que d'introduire de nouvelles classes CSS.
 *
 * finished/elapsedSeconds sont calcules par le parent (TerminalView) a partir des refs
 * taskStartMsRef/taskFinishedAtMsRef — ce composant est purement un rendu, aucune logique
 * de timer ici.
 */
function TerminalTaskStart({ finished, elapsedSeconds }: { finished: boolean; elapsedSeconds: number }) {
  return (
    <div className="terminal-block terminal-tool-progress">
      <span className="terminal-progress-icon">{finished ? '✓' : '▶'}</span>
      <span>{finished ? 'Cowboy terminé' : 'Cowboy lancé'}</span>
      <span className="terminal-progress-time">{'(' + elapsedSeconds + 's)'}</span>
    </div>
  )
}

/**
 * Notification de fin (task_notification) — bloc distinct de TerminalTaskStart : celui-ci
 * porte le contenu utile du SDK (summary/status resume par le daemon dans `content`, cf.
 * index.ts:779-785). Reutilise .terminal-tool-summary (label) + .terminal-tool-output
 * (contenu, si non vide) — memes classes que le reste du fichier, zero CSS nouveau.
 */
function TerminalTaskNotification({ content }: { content: string }) {
  const trimmed = content.trim()
  return (
    <>
      <div className="terminal-block terminal-tool-summary">{'Cowboy terminé'}</div>
      {trimmed !== '' && (
        <pre className="terminal-block terminal-tool-output">{linkifyText(truncateOutput(trimmed))}</pre>
      )}
    </>
  )
}

function TerminalThinking({ content }: { content: string }) {
  return <div className="terminal-block terminal-thinking">{content}</div>
}

function TerminalStatus({ message }: { message: string }) {
  return <div className="terminal-block terminal-status">{message}</div>
}

/**
 * Rendu des 5 types V2 (hook_start/hook_result/compact/api_retry/system_init) — deja emis par
 * le daemon (system/daemon-llm/index.ts:746-800) mais jusqu'ici invisibles (default: return null).
 * Reutilisent tous .terminal-status (ligne grise discrete, deja le style le plus sobre du
 * fichier) : zero CSS nouveau. journal-minidev-tout-afficher-terminal-pulsar-2026-08-11.
 *
 * Labellisation "démarré"/"terminé" (audit-taxonomie-erreurs-terminal-pulsar-20260811, point
 * HOOKS) : le SDK émet parfois 2x hook_started + 2x hook_response pour un même SessionStart:resume
 * (doublon côté SDK, hors périmètre — décision Fabrice explicite : PAS de dédup cosmétique, risque
 * de grouper des events non-groupables). Les deux events SDK restent affichés intégralement, même
 * en double ; seul le libellé change pour qu'on distingue sans ambiguïté lequel est le départ
 * (hook_start) et lequel est la réponse/fin (hook_result).
 */
function TerminalHookStart({ hookName, hookEvent }: { hookName: string; hookEvent: string }) {
  return <div className="terminal-block terminal-status">{`⚙ Hook démarré : ${hookName} (${hookEvent})`}</div>
}

function TerminalHookResult({ hookName, exitCode, outcome }: { hookName: string; exitCode: number; outcome: string }) {
  const style = exitCode !== 0 ? { color: '#e74c3c' } : undefined
  return <div className="terminal-block terminal-status" style={style}>{`⚙ Hook terminé : ${hookName} → ${outcome} (exit ${exitCode})`}</div>
}

function TerminalCompact({ message }: { message: string }) {
  return <div className="terminal-block terminal-status">{`↺ ${message}`}</div>
}

function TerminalApiRetry({ attempt, waitSeconds }: { attempt: number; waitSeconds: number }) {
  return <div className="terminal-block terminal-status">{`⏱ Nouvelle tentative API (#${attempt}, ${waitSeconds}s)`}</div>
}

function TerminalSystemInit({ model }: { model: string }) {
  return <div className="terminal-block terminal-status">{`● Modele : ${model}`}</div>
}

/**
 * Bloc d'erreur client-only (TERM) — journal-minidev-tout-afficher-terminal-pulsar-2026-08-11.
 * Reutilise a l'identique les 2 classes deja employees par TerminalToolOutput en cas d'erreur
 * detectee (isErrorOutput) : zero CSS nouveau. Source : terminal.store.ts clientErrors, alimente
 * par messages.handler.ts:onError (message:error) et status.handler.ts:onStatusChanged
 * (status:changed avec champ error) — les 2 canaux qui portaient deja l'erreur sur le fil sans
 * jamais la rendre visible nulle part cote client.
 */
function TerminalErrorBlock({ content }: { content: string }) {
  return (
    <pre className="terminal-block terminal-tool-output terminal-tool-output-error">
      {'⚠ Erreur : '}{linkifyText(content)}
    </pre>
  )
}

function TerminalResult({ totalCostUsd, durationMs, numTurns, tokenUsage }: {
  totalCostUsd: number
  durationMs: number
  numTurns: number
  tokenUsage?: { input: number; output: number }
}) {
  const durationSec = (durationMs / 1000).toFixed(1)
  const cost = totalCostUsd.toFixed(4)
  return (
    <div className="terminal-block terminal-result">
      <span className="terminal-result-item">${cost}</span>
      <span className="terminal-result-sep">{'\u00b7'}</span>
      <span className="terminal-result-item">{durationSec}s</span>
      <span className="terminal-result-sep">{'\u00b7'}</span>
      <span className="terminal-result-item">{numTurns} turn{numTurns !== 1 ? 's' : ''}</span>
      {tokenUsage && (
        <>
          <span className="terminal-result-sep">{'\u00b7'}</span>
          <span className="terminal-result-item">{tokenUsage.input.toLocaleString()}in / {tokenUsage.output.toLocaleString()}out</span>
        </>
      )}
    </div>
  )
}

/**
 * Message user (BUG1 dogfood Pulsar, journal #10530/#22698) \u2014 TERM n'affichait que les
 * events assistant/outil, jamais les messages envoyes par l'utilisateur. Bloc dedie,
 * reutilise les classes CSS existantes terminal-block + terminal-user (terminal.css,
 * meme token --color-terminal-user que l'ancien rendu ligne-par-ligne) : aucune nouvelle
 * regle CSS necessaire.
 */
function TerminalUserMessage({ content }: { content: string }) {
  return <pre className="terminal-block terminal-user">{'> '}{linkifyText(content)}</pre>
}

/** Type d'affichage TERM elargi au message user \u2014 local a ce fichier, ne touche pas le
 * type partage TerminalEvent (daemon/serveur/BDD n'ont pas de notion de "user_message"
 * cote terminal, c'est une derivation purement client-side de trpc.messages.list).
 * 'error_message' \u2014 meme principe, derivation purement client-side de
 * terminal.store.ts:clientErrors (journal-minidev-tout-afficher-terminal-pulsar-2026-08-11). */
type TerminalDisplayEvent = TerminalEvent | { type: 'user_message'; content: string } | { type: 'error_message'; content: string }

/**
 * Merge consecutive tool_progress events for the same toolUseId,
 * keeping only the latest one (replace-in-place behavior, throttle d'un compteur
 * de progression — pas une reconciliation de contenu).
 *
 * TERM doit montrer les events bruts par seq, dans l'ordre d'emission, texte et
 * outils intercales SANS regroupement (journal-minidev-terminal-raw-by-seq-pulsar-
 * 2026-08-10) : les deltas 'text' et 'thinking' ne sont plus concatenes ici — chaque
 * event brut reste son propre bloc rendu. Avant ce fix, la concatenation de N deltas
 * consecutifs de meme type en 1 seul bloc produisait visuellement l'effet d'un
 * message reconstitue (indiscernable de la fenetre MESSAGES/DISC), meme si l'ordre
 * lui-meme (calcule en amont dans allEvents, tri par seq) restait correct.
 *
 * 'user_message' (BUG1) ne matche pas le `if` tool_progress ci-dessous : il tombe
 * toujours sur le `result.push(event)` par defaut, en pass-through pur.
 */
function mergeProgressEvents(events: TerminalDisplayEvent[]): TerminalDisplayEvent[] {
  const result: TerminalDisplayEvent[] = []
  for (const event of events) {
    if (event.type === 'tool_progress') {
      const lastIdx = result.length - 1
      const last = result[lastIdx]
      if (last && last.type === 'tool_progress' && last.toolUseId === event.toolUseId) {
        result[lastIdx] = event
        continue
      }
    }
    result.push(event)
  }
  return result
}

/**
 * Timers locaux (client-only) des taches cowboy en cours/terminees — journal #10687.
 * startMsById : taskId -> timestamp de lancement (createdAtMs reel pour un event
 * historique, Date.now() a l'arrivee pour un event live — cf. TerminalView).
 * finishedAtMsById : taskId -> timestamp de fin (meme logique, alimente par
 * task_notification). Absence de finishedAtMsById pour un taskId = tache toujours active.
 */
type TaskTimers = {
  startMsById: Map<string, number>
  finishedAtMsById: Map<string, number>
}

function TerminalEventRenderer({ event, taskTimers }: { event: TerminalDisplayEvent; taskTimers: TaskTimers }) {
  switch (event.type) {
    case 'text':
      return <TerminalTextBlock content={event.content} />
    case 'tool_start':
      return <TerminalToolStart toolName={event.toolName} input={event.input} />
    case 'tool_output':
      return <TerminalToolOutput content={event.content} />
    case 'tool_progress':
      return <TerminalToolProgress toolName={event.toolName} elapsedSeconds={event.elapsedSeconds} />
    case 'tool_summary':
      return <TerminalToolSummary summary={event.summary} />
    case 'thinking':
      return <TerminalThinking content={event.content} />
    case 'status':
      return <TerminalStatus message={event.message} />
    case 'result':
      return <TerminalResult totalCostUsd={event.totalCostUsd} durationMs={event.durationMs} numTurns={event.numTurns} tokenUsage={event.tokenUsage} />
    case 'user_message':
      return <TerminalUserMessage content={event.content} />
    case 'error_message':
      return <TerminalErrorBlock content={event.content} />
    case 'hook_start':
      return <TerminalHookStart hookName={event.hookName} hookEvent={event.hookEvent} />
    case 'hook_result':
      return <TerminalHookResult hookName={event.hookName} exitCode={event.exitCode} outcome={event.outcome} />
    case 'compact':
      return <TerminalCompact message={event.message} />
    case 'api_retry':
      return <TerminalApiRetry attempt={event.attempt} waitSeconds={event.waitSeconds} />
    case 'system_init':
      return <TerminalSystemInit model={event.model} />
    case 'task_start': {
      const startMs = taskTimers.startMsById.get(event.taskId)
      const finishedMs = taskTimers.finishedAtMsById.get(event.taskId)
      const elapsedSeconds = startMs === undefined
        ? 0
        : Math.max(0, Math.round(((finishedMs ?? Date.now()) - startMs) / 1000))
      return <TerminalTaskStart finished={finishedMs !== undefined} elapsedSeconds={elapsedSeconds} />
    }
    case 'task_notification':
      return <TerminalTaskNotification content={event.content} />
    default:
      return null
  }
}

/**
 * Convertit les events depuis la BDD (payload stocké en JSON.stringify) vers TerminalEventWithSeq[].
 * Le payload est stocké comme string JSON dans la colonne JSONB — PostgreSQL le retourne déjà parsé.
 * On cast directement : la structure est garantie par l'insertion (onComplete dans messages.ts).
 * seq (colonne terminal_events.seq) est porté séparément du payload — voir migration 005 et
 * message.persistence.ts (journal #10485) : null pour les lignes écrites avant cette migration.
 *
 * row.seq est typé number|null cote TS, mais terminal_events.seq est un BIGINT Postgres : sans
 * type parser dedie (oid 20), node-postgres le renvoie par defaut en STRING (evite la perte de
 * precision au-dela de 2^53, cf. doc node-postgres). Verifie 1re main : aucun setTypeParser(20,...)
 * nulle part dans le repo (grep exhaustif server/ + system/, seul oid 1114 est patche dans
 * kysely.ts). Le seq LIVE (canal Centrifugo, JSON) arrive lui en number natif — sans cette
 * normalisation, le dedup par seq (Set<number>) dans allEvents ci-dessous casse silencieusement
 * sur la fenetre de recouvrement historique/live (meme event, deux types JS differents,
 * seen.has(123) vs seen.add("123") ne matchent jamais). Number() est sans cout si pg renvoyait
 * deja un number.
 *
 * createdAtMs (BUG1, journal #10530) : timestamp reel (colonne terminal_events.created_at,
 * meme horloge Postgres que messages.created_at) porte EN PLUS de seq, pour permettre
 * d'intercaler les messages user au bon endroit chronologique parmi les events HISTORIQUES
 * (deja persistes). Necessaire car termSeq (daemon) n'est PAS comparable a created_at*1000 :
 * il n'est reseede qu'a la (re)creation d'une session SDK (handleStart/handleResume explicite),
 * PAS a chaque tour — deux messages envoyes dans la meme generation (cas courant, < 3 min
 * d'ecart, worker jamais tue par la grace period) ont un created_at qui avance en temps reel
 * alors que termSeq n'avance que de quelques unites par tour. Comparer created_at*1000 a un
 * seq de tour N+1 place donc a tort le message APRES les events de son propre tour des le
 * 2e tour d'une meme generation (verifie code : system/daemon-llm/index.ts:533,1102,1366,
 * handleSend:1169 n'appelle resumeTransparently — donc ne reseede rien — que si !isAlive).
 * createdAtMs n'existe donc que pour les events HISTORIQUES ; les events LIVE n'ont pas de
 * timestamp reel cote client (seul le seq daemon existe) — inutile d'en fabriquer un : le
 * merge (allEvents dans TerminalView) exploite le fait que liveEvents ne contient jamais
 * qu'un seul tour a la fois (reset a chaque status idle/error/closed, terminal.store).
 */
function parseStoredEvents(
  rows: Array<{ type: string; payload?: unknown; created_at: Date | string; seq: number | string | null }>,
): Array<TerminalEventWithSeq & { createdAtMs: number }> {
  return rows
    .map(row => {
      // PostgreSQL retourne le JSONB déjà parsé — mais on a inséré JSON.stringify(event)
      // donc payload est une string JSON ou un objet selon le driver
      try {
        if (row.payload === undefined || row.payload === null) return null
        const parsed = typeof row.payload === 'string'
          ? (JSON.parse(row.payload) as TerminalEvent)
          : (row.payload as TerminalEvent)
        const seq = (row.seq === null || row.seq === undefined) ? null : Number(row.seq)
        const createdAtMs = new Date(row.created_at).getTime()
        return { ...parsed, seq, createdAtMs }
      } catch {
        return null
      }
    })
    .filter((e): e is TerminalEventWithSeq & { createdAtMs: number } => e !== null)
}

export function TerminalView({ sessionId, messageSentSignal }: { sessionId: string; messageSentSignal?: number }) {
  const session = useMessagesStore(s => s.sessions[sessionId])
  const terminalSession = useTerminalStore(s => s.sessions[sessionId])
  const loadHistoricalEvents = useTerminalStore(s => s.loadHistoricalEvents)
  const liveTerminalEvents = terminalSession?.liveEvents ?? []
  const endRef = useRef<HTMLDivElement>(null)

  // Stick-to-bottom (lacher-prise autoscroll) -- extrait dans un hook partage avec
  // MessageList.tsx (journal BDD #26422). Consequence assumee, inchangee : a chaque remount
  // de ce composant (changement d'onglet de conversation via key={sessionId} dans
  // ChatPage.tsx, OU bascule TERM/DISC dans ChatWidget.tsx qui demonte/remonte TerminalView),
  // la vue repart pinnee en bas -- comportement volontaire, valide en PHASE3 (journal BDD
  // #26277), pas un oubli.
  const { isPinnedToBottom, handleScroll, scrollToBottom } = useStickToBottom(endRef, messageSentSignal)

  // Charger les events historiques depuis la BDD au mount et au changement de session
  const { data: storedEventsRows } = trpc.messages.terminalEvents.listBySession.useQuery(
    { sessionId },
    { enabled: !!sessionId },
  )

  // Events historiques avec timestamp réel (BUG1, journal #10530/#22698) — source canonique
  // pour le merge ci-dessous. Recalcule le même parseStoredEvents (pure function) que le
  // useEffect de sync store juste en dessous — le store reste alimenté pour compatibilité,
  // mais son type déclaré (TerminalEventWithSeq[]) ne porte pas createdAtMs, d'où le passage
  // par cette valeur locale plutôt que par le sélecteur du store pour le merge.
  const historicalWithTs = useMemo(
    () => (storedEventsRows ? parseStoredEvents(storedEventsRows) : []),
    [storedEventsRows],
  )

  // Synchroniser les events historiques dans le store quand la query retourne des données
  useEffect(() => {
    if (storedEventsRows) {
      loadHistoricalEvents(sessionId, historicalWithTs)
    }
  }, [storedEventsRows, historicalWithTs, sessionId, loadHistoricalEvents])

  // Messages user (BUG1, journal #10530/#22698) — même requête/queryKey que MessageList.tsx:41-44
  // (procédure tRPC déjà utilisée ailleurs dans l'app pour DISC, cache partagé si les deux vues
  // ont été montées). role==='user' uniquement ; contenu vide/blanc exclu — pas de bloc TERM
  // vide (point-2-PHASE3).
  const { data: messagesRows } = trpc.messages.list.useQuery(
    { sessionId, limit: 50 },
    { enabled: !!sessionId, staleTime: Infinity, refetchOnWindowFocus: false, refetchOnReconnect: false },
  )

  const userEvents = useMemo(() => {
    return (messagesRows ?? [])
      .filter(m => m.role === 'user' && m.content.trim() !== '')
      .map(m => ({
        type: 'user_message' as const,
        content: m.content,
        createdAtMs: new Date(m.created_at).getTime(),
      }))
      .sort((a, b) => a.createdAtMs - b.createdAtMs)
  }, [messagesRows])

  // Erreurs client-only (TERM) — journal-minidev-tout-afficher-terminal-pulsar-2026-08-11.
  // Source : terminal.store.ts:clientErrors, alimenté par messages.handler.ts:onError
  // (message:error, y compris sans texte préalable) et status.handler.ts:onStatusChanged
  // (status:changed avec champ error). Jamais lu par MessageList.tsx/DISC — lecture seule ici,
  // aucun setter appelé depuis ce composant.
  const errorEvents = useMemo(() => {
    return (terminalSession?.clientErrors ?? [])
      .map(e => ({
        type: 'error_message' as const,
        content: e.message,
        createdAtMs: e.atMs,
      }))
  }, [terminalSession])

  // Flux "hors terminal" fusionné et trié par timestamp — messages user + erreurs client-only,
  // même intercalation par createdAtMs que le mécanisme BUG1 existant (voir plus bas).
  const sideEvents = useMemo(() => {
    return [...userEvents, ...errorEvents].sort((a, b) => a.createdAtMs - b.createdAtMs)
  }, [userEvents, errorEvents])

  // Merger events historiques (tours précédents) + events live (tour en cours), triés par seq
  // (fix ordre TERM, journal #10485) — le seq est assigné par le daemon à l'émission réelle,
  // pas à l'écriture BDD, donc résiste à un tour rejoué tardivement (buffer+replay après un
  // déploiement web-only, cf. audit #10481). Dédup par seq : couvre le replay de recovery
  // Centrifugo qui peut réinjecter des events déjà affichés (idempotence, cf. #11050 B.1) — un
  // rejeu porte désormais le MÊME seq qu'à l'émission d'origine (contrairement à l'ancien
  // eventId, régénéré à chaque broadcast). Fallback : les événements sans seq (legacy
  // pré-migration, ou edge case daemon sans état de session) gardent leur ordre d'arrivée
  // relatif via un tri stable sur -Infinity.
  //
  // Intercalage des messages user ET des erreurs client-only (sideEvents, BUG1 + journal-minidev-
  // tout-afficher-terminal-pulsar-2026-08-11) : PAS de comparaison seq-vs-
  // created_at*1000 — échelles incomparables dès le 2e tour d'une même génération (voir le
  // commentaire de parseStoredEvents ci-dessus). Les events HISTORIQUES portent un createdAtMs
  // réel (même horloge Postgres que messages.created_at) → tri fiable events-historiques /
  // messages par vrai timestamp. Les events LIVE n'ont pas de createdAtMs (pas de timestamp
  // réel côté client) mais ne représentent jamais qu'un seul tour à la fois (reset à chaque
  // status idle/error/closed, terminal.store) : tout message pas encore placé au moment où la
  // boucle atteint le premier event live est donc nécessairement celui qui a déclenché CE
  // tour — poussé juste avant, sans avoir besoin de comparer de timestamp.
  const allEvents = useMemo(() => {
    const combinedTerminal: Array<TerminalEventWithSeq & { createdAtMs?: number }> = [
      ...historicalWithTs,
      ...liveTerminalEvents,
    ]
    const seen = new Set<number>()
    const dedupedTerminal: Array<TerminalEventWithSeq & { createdAtMs?: number }> = []
    for (const event of combinedTerminal) {
      if (event.seq !== null && event.seq !== undefined) {
        if (seen.has(event.seq)) continue
        seen.add(event.seq)
      }
      dedupedTerminal.push(event)
    }
    dedupedTerminal.sort((a, b) => (a.seq ?? Number.NEGATIVE_INFINITY) - (b.seq ?? Number.NEGATIVE_INFINITY))

    const merged: TerminalDisplayEvent[] = []
    let si = 0
    for (const ev of dedupedTerminal) {
      while (si < sideEvents.length && (ev.createdAtMs === undefined || sideEvents[si].createdAtMs <= ev.createdAtMs)) {
        merged.push(sideEvents[si])
        si++
      }
      merged.push(ev)
    }
    while (si < sideEvents.length) {
      merged.push(sideEvents[si])
      si++
    }
    return merged
  }, [historicalWithTs, liveTerminalEvents, sideEvents])

  const mergedEvents = useMemo(
    () => mergeProgressEvents(allEvents),
    [allEvents],
  )

  // --- Timers locaux task_start/task_notification (journal #10687) ---
  // Purement client-side : aucune ecriture de store, aucun changement daemon/serveur/type.
  // Deux refs mutables (taskId -> timestamp ms), peuplees par 2 useEffect distincts qui
  // lisent les sources BRUTES (historicalWithTs / liveTerminalEvents, typees avec
  // createdAtMs pour les premiers) plutot que mergedEvents (type TerminalDisplayEvent[]
  // qui ne porte pas createdAtMs cote TS) — evite tout cast.
  const taskStartMsRef = useRef<Map<string, number>>(new Map())
  const taskFinishedAtMsRef = useRef<Map<string, number>>(new Map())

  // Events historiques : createdAtMs reel (meme horloge Postgres que le reste du fichier,
  // cf. parseStoredEvents) — le vrai instant de lancement/fin, pas une approximation.
  useEffect(() => {
    for (const ev of historicalWithTs) {
      if (ev.type === 'task_start' && !taskStartMsRef.current.has(ev.taskId)) {
        taskStartMsRef.current.set(ev.taskId, ev.createdAtMs)
      }
      if (ev.type === 'task_notification' && !taskFinishedAtMsRef.current.has(ev.taskId)) {
        taskFinishedAtMsRef.current.set(ev.taskId, ev.createdAtMs)
      }
    }
  }, [historicalWithTs])

  // Events live : pas de timestamp reel cote client (cf. commentaire parseStoredEvents
  // plus haut dans ce fichier) — Date.now() capture a l'arrivee sert d'approximation du
  // "depuis quand" local, exactement le meme principe que le timer de Claude Code CLI.
  useEffect(() => {
    for (const ev of liveTerminalEvents) {
      if (ev.type === 'task_start' && !taskStartMsRef.current.has(ev.taskId)) {
        taskStartMsRef.current.set(ev.taskId, Date.now())
      }
      if (ev.type === 'task_notification' && !taskFinishedAtMsRef.current.has(ev.taskId)) {
        taskFinishedAtMsRef.current.set(ev.taskId, Date.now())
      }
    }
  }, [liveTerminalEvents])

  // Une tache est active si elle a un task_start sans task_notification correspondant,
  // toutes sources confondues (historique + live) — sert de garde au ticker ci-dessous.
  const hasActiveTask = useMemo(() => {
    const startedIds = new Set<string>()
    const finishedIds = new Set<string>()
    for (const ev of historicalWithTs) {
      if (ev.type === 'task_start') startedIds.add(ev.taskId)
      if (ev.type === 'task_notification') finishedIds.add(ev.taskId)
    }
    for (const ev of liveTerminalEvents) {
      if (ev.type === 'task_start') startedIds.add(ev.taskId)
      if (ev.type === 'task_notification') finishedIds.add(ev.taskId)
    }
    for (const id of startedIds) {
      if (!finishedIds.has(id)) return true
    }
    return false
  }, [historicalWithTs, liveTerminalEvents])

  // Ticker : force un re-render chaque seconde UNIQUEMENT tant qu'une tache est active,
  // pour que l'ecoule affiche par TerminalTaskStart avance visuellement. S'arrete (cleanup)
  // des qu'il n'y a plus de tache active — pas de setInterval qui tourne pour rien.
  const [, forceTaskTick] = useState(0)
  useEffect(() => {
    if (!hasActiveTask) return
    const id = setInterval(() => forceTaskTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [hasActiveTask])

  const taskTimers = { startMsById: taskStartMsRef.current, finishedAtMsById: taskFinishedAtMsRef.current }

  // Auto-scroll on new content -- SEULEMENT si pinne en bas (stick-to-bottom, journal BDD
  // #26277). isPinnedToBottom est dans les deps DELIBEREMENT (vigilance PHASE3, tranchee) :
  // sans lui, un retour manuel en bas (scroll natif, sans clic sur le bouton "aller en bas")
  // ne redeclencherait cet effet qu'au PROCHAIN event recu -- React ne re-execute un effet
  // que si une dep declaree change. L'utilisateur devrait alors attendre un nouvel event pour
  // que le scroll se recolle pile en bas, au lieu d'un recollage immediat. Aucun risque de
  // boucle : cet effet ne modifie jamais isPinnedToBottom (lecture seule), et
  // setIsPinnedToBottom(true) sur une valeur deja true ne redeclenche pas de re-render (bail-out
  // React sur etat identique).
  useEffect(() => {
    if (!isPinnedToBottom) return
    endRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior })
  }, [mergedEvents, session?.streamingContent, isPinnedToBottom])

  return (
    <div className="terminal-view" onScroll={handleScroll}>
      {/* Live terminal events (current turn + historical) */}
      {mergedEvents.map((event, i) => (
        <TerminalEventRenderer key={i} event={event} taskTimers={taskTimers} />
      ))}

      {/* Streaming cursor */}
      {session?.isStreaming && !mergedEvents.length && (
        <pre className="terminal-line terminal-streaming">
          {`[assistant] ${session.streamingContent}\u2588`}
        </pre>
      )}

      <div ref={endRef} />

      {/* Bouton "aller en bas" -- sticky (pas absolute, vigilance PHASE3, journal BDD #26277) :
          .terminal-view est le conteneur scrollable lui-meme, un enfant absolute s'y
          positionnerait par rapport au contenu total (invisible precisement quand on a
          remonte). position:sticky reste visible dans le viewport visible du scroller, verifie
          empiriquement (harnais isole + screenshot, journal BDD #26277). */}
      {!isPinnedToBottom && (
        <button
          type="button"
          className="terminal-scroll-to-bottom"
          onClick={scrollToBottom}
          aria-label="Aller en bas de la conversation"
        >
          {'\u2193 Reprendre en bas'}
        </button>
      )}
    </div>
  )
}
